import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/testIds";
import { Landmark, ArrowRight, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) {
      toast.success("Bienvenue sur Fonbex");
      // Redirect logic: pure super admin (no membership) → /admin, else → /app
      const data = result.data || {};
      const platformRole = data.user?.platform_role;
      const hasMemberships = (data.memberships || []).length > 0;
      const requestedFrom = location.state?.from?.pathname;
      let target;
      if (requestedFrom) {
        target = requestedFrom;
      } else if (platformRole === "platform_admin" && !hasMemberships) {
        target = "/admin";
      } else {
        target = "/app/assistant";
      }
      navigate(target, { replace: true });
    } else {
      setError(result.error || "Impossible de se connecter");
    }
  };

  const useDemo = async () => {
    try {
      await api.post("/demo/login");
      toast.success("Connexion démo réussie");
      navigate("/app/assistant", { replace: true });
      // trigger auth context refresh
      window.location.reload();
    } catch { toast.error("Compte de démonstration indisponible"); }
  };

  return (
    <AuthShell>
      <h1 className="font-serif text-3xl sm:text-4xl">Ravi de vous revoir.</h1>
      <p className="mt-2 text-muted-foreground">Accédez à votre espace patrimonial.</p>

      {location.state?.sessionExpired && (
        <div className="mt-6 rounded-2xl border border-amber-300/50 bg-amber-50 p-3 text-sm text-amber-900"
             data-testid="login-session-expired-banner">
          Votre session a expiré. Veuillez vous reconnecter.
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="email">Adresse e-mail</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid={TID.loginEmail} required autoComplete="email" className="mt-1.5" />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <Label htmlFor="password">Mot de passe</Label>
            <Link to="/forgot-password" className="text-xs text-accent link-underline">Oublié ?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                 data-testid={TID.loginPassword} required autoComplete="current-password" className="mt-1.5" />
        </div>
        {error && (
          <div data-testid={TID.loginError} className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <Button type="submit" disabled={loading} className="btn-pill w-full" data-testid={TID.loginSubmit}>
          {loading ? "Connexion…" : (<>Se connecter <ArrowRight className="ml-2 h-4 w-4" /></>)}
        </Button>
      </form>

      <div className="mt-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">ou</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      {/* Demo button hidden in production (fonbex.fr) */}
      {typeof window !== "undefined" && !/(^|\.)(patripilot|fonbex)\.fr$/i.test(window.location.hostname) && (
        <Button type="button" variant="outline" onClick={useDemo}
                className="btn-pill w-full mt-4"
                data-testid="login-demo-btn">
          Utiliser le compte de démonstration
        </Button>
      )}

      <p className="mt-8 text-sm text-muted-foreground text-center">
        Pas encore de compte ? <Link to="/register" className="text-accent link-underline font-medium">Créer une organisation</Link>
      </p>

      <div className="mt-8 pt-6 border-t border-border">
        <Link
          to="/tenant/login"
          data-testid="tenant-portal-entry"
          className="group flex items-center justify-between gap-3 rounded-2xl border border-accent/30 bg-accent/5 hover:bg-accent/10 transition-colors px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/20 group-hover:bg-accent/30 transition-colors">
              <HomeIcon className="h-4 w-4 text-accent-foreground" />
            </div>
            <div>
              <div className="text-sm font-heading text-foreground">Vous êtes locataire ?</div>
              <div className="text-xs text-muted-foreground">Accéder à votre espace locataire</div>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-accent group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: BRAND.colors.night }}>
        <Link to="/" className="flex items-center gap-3 text-white relative z-10">
          <BRAND.logo.Symbol size={40} tone="light" />
          <div className="leading-tight">
            <div className="text-2xl" style={{
              fontFamily: "'Cabinet Grotesk', 'Space Grotesk', system-ui, sans-serif",
              fontWeight: 700, letterSpacing: "0.02em",
            }}>{BRAND.nameUppercase}</div>
            <div className="text-[9px] uppercase tracking-[0.25em]"
                 style={{ color: "rgba(255,255,255,0.55)" }}>
              Gestion immobilière
            </div>
          </div>
        </Link>
        <div className="relative z-10 text-white/90 max-w-md">
          <blockquote className="text-xl leading-snug" style={{
            fontFamily: "'Cabinet Grotesk', 'Space Grotesk', system-ui, sans-serif",
            fontWeight: 500,
          }}>
            &laquo;&nbsp;{BRAND.slogan}&nbsp;&raquo;
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full" style={{ backgroundColor: BRAND.colors.primary }} />
            <div className="text-sm">
              <div className="text-white" style={{ fontWeight: 600 }}>{BRAND.name}</div>
              <div className="text-white/60">La plateforme de gestion immobilière</div>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 30%, hsl(36 43% 50% / .3), transparent 50%)" }} />
      </div>
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}
